import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import LuvmeIcon from "@/components/LuvmeIcon";
import {
  Sparkles, ArrowRight, UserPlus, Image, DollarSign, Wallet, Menu, X,
  Palette, Lock, Radio, Gift, MessageSquare, Check, Minus, ShieldCheck, BadgeCheck,
  Zap, SlidersHorizontal, Clock, Headphones, BarChart3,
} from "lucide-react";
import { IMAGES } from "@/lib/images";
import { Reveal, CountUp, Particles, ScrollProgress, WordReveal, TiltCard, Magnetic, Sheen, CursorGlow, ZoomImage } from "@/components/cinematic";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const NAV = [
  { label: "Chi siamo", href: "#chi-siamo" },
  { label: "Per i creator", href: "#come-funziona" },
];

const PILLARS = [
  { icon: DollarSign, title: "Solo 15% di commissione", text: "Ti resta l'85% dei tuoi guadagni: molto più che sulle piattaforme al 20% o più." },
  { icon: Zap, title: "Pagamenti rapidi e trasparenti", text: "Nessun costo nascosto. Vedi ogni incasso in chiaro e ricevi i tuoi guadagni entro 48 ore." },
  { icon: SlidersHorizontal, title: "Le tue regole", text: "Decidi tu prezzi, contenuti e pubblico. Hai il pieno controllo del tuo spazio." },
];

const WHY_LUVME = [
  { icon: Clock, title: "Pagamenti in 48 ore", text: "Niente attese infinite. Ricevi i tuoi guadagni entro 48 ore dalla richiesta." },
  { icon: Wallet, title: "Soglia di prelievo bassa", text: "Prelievi anche piccoli importi, senza soglie minime che penalizzano chi sta iniziando." },
  { icon: Headphones, title: "Supporto umano reale", text: "Un team vero che risponde, non un bot. Assistenza dedicata per ogni creator." },
  { icon: BarChart3, title: "Trasparenza sui dati", text: "Analytics dettagliati su chi sono i tuoi fan, cosa converte meglio, quando pubblicare." },
  { icon: ShieldCheck, title: "Nessuna sospensione arbitraria", text: "Politiche di moderazione chiare e un processo di appello reale in caso di problemi." },
];

const STEPS = [
  { icon: UserPlus, title: "Crea il profilo", text: "Registrati come creator e personalizza il tuo spazio in pochi minuti." },
  { icon: Image, title: "Pubblica contenuti", text: "Carica foto, album e post esclusivi. Decidi cosa è gratis e cosa premium." },
  { icon: DollarSign, title: "Imposta i tuoi prezzi", text: "Abbonamento mensile o contenuti sbloccabili singolarmente. Sei tu a decidere." },
  { icon: Wallet, title: "Incassa di più", text: "Tratteniamo solo il 15%. Il resto è tuo. Pagamenti rapidi e trasparenti." },
];

const FEATURES = [
  { icon: Palette, title: "Profilo personalizzabile", text: "Cover, avatar, bio, link social e tema colore su misura per il tuo brand." },
  { icon: Lock, title: "Contenuti e album a pagamento", text: "Sblocca i tuoi post premium con un tocco. Gestisci ogni contenuto." },
  { icon: Radio, title: "Dirette live", text: "Connettiti con i tuoi fan in tempo reale. (In arrivo)" },
  { icon: Gift, title: "Regali virtuali", text: "I fan ti supportano con regali durante le live. (In arrivo)" },
  { icon: MessageSquare, title: "Messaggi privati", text: "Chat diretta e contenuti su richiesta con i tuoi abbonati. (In arrivo)" },
];

const FAQ = [
  { q: "Come funzionano i pagamenti?", a: "I fan pagano abbonamenti o sbloccano singoli contenuti. Luvme trattiene solo il 15% di commissione, tra le più basse del settore, e i guadagni vengono accreditati al creator con pagamenti entro 48 ore." },
  { q: "I miei dati e la mia privacy sono al sicuro?", a: "Sì. Proteggiamo i tuoi dati con i massimi standard di sicurezza. Puoi controllare la visibilità di ogni contenuto e decidere chi può accedervi." },
  { q: "Come divento creator?", a: "Registrati scegliendo il profilo Creator, completa l'onboarding con foto, bio e prezzi, e inizia a pubblicare. La verifica dell'età è obbligatoria." },
  { q: "Perché la commissione è più bassa?", a: "Crediamo che i creator debbano tenere di più di ciò che guadagnano. Con il nostro modello al 15% incassi molto più che sulle piattaforme al 20% o più — e in cambio offriamo pagamenti rapidi e supporto reale." },
];

const COMPARISON = [
  { label: "Commissione piattaforma", luvme: "Solo 15%", others: "20% o più" },
  { label: "Profilo personalizzabile", luvme: true, others: true },
  { label: "Abbonamenti + sblocco singolo", luvme: true, others: true },
  { label: "Pagamenti entro 48h", luvme: true, others: false },
  { label: "Soglia prelievo bassa", luvme: true, others: false },
  { label: "Supporto umano dedicato", luvme: true, others: false },
  { label: "Pagamenti trasparenti", luvme: true, others: false },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const reduce = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const { scrollY } = useScroll();
  // Parallax amounts are disabled under reduced-motion and lighter on mobile.
  const bgShift = reduce ? 0 : isMobile ? 40 : 90;   // hero bg moves slower (down)
  const textShift = reduce ? 0 : isMobile ? 18 : 40; // foreground text/content
  const glowShift = reduce ? 0 : -160;

  const glowY = useTransform(scrollY, [0, 700], [0, glowShift]);
  const imageY = useTransform(scrollY, [0, 700], [0, textShift]);
  const textY = useTransform(scrollY, [0, 700], [0, textShift]);
  const heroBgY = useTransform(scrollY, [0, 700], [0, bgShift]);
  const heroDark = useTransform(scrollY, [0, 600], [0, reduce ? 0 : 0.55]);

  return (
    <div className="relative min-h-screen bg-[#0D0D0D] text-white overflow-x-hidden">
      <ScrollProgress />
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <LuvmeIcon className="w-6 h-6 sm:w-7 sm:h-7" />
            <span className="text-2xl font-black font-heading tracking-tight">Luvme</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-sm text-white/70 hover:text-white transition-colors duration-300">
                {n.label}
              </a>
            ))}
            <Link to="/login" data-testid="header-login" className="text-sm text-white/70 hover:text-white transition-colors duration-300">
              Accedi
            </Link>
            <Magnetic>
              <Link
                to="/register"
                data-testid="header-register"
                className="group relative overflow-hidden inline-flex items-center px-5 py-2 rounded-full bg-[#E91E8C] text-white text-sm font-semibold hover:bg-[#D8157B] hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all duration-300"
              >
                <Sheen />
                Registrati
              </Link>
            </Magnetic>
          </nav>

          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden glass border-t border-white/10 px-5 py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="text-white/80">{n.label}</a>
            ))}
            <Link to="/login" className="text-white/80">Accedi</Link>
            <Link to="/register" className="px-5 py-2 rounded-full bg-[#E91E8C] text-center font-semibold">Registrati</Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-28 px-5 sm:px-8 grain overflow-hidden">
        <div className="mesh-gradient absolute inset-0 opacity-70 pointer-events-none" />
        <Particles count={20} />
        <motion.div
          style={{ y: glowY }}
          className="absolute top-10 right-0 w-[45vw] h-[45vw] max-w-[640px] max-h-[640px] rounded-full opacity-50 blur-3xl animate-float-slow pointer-events-none"
        >
          <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, rgba(233,30,140,0.55), transparent 70%)" }} />
        </motion.div>

        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="hero-bg">
          <motion.div className="absolute inset-0" style={{ y: heroBgY, scale: 1.12 }}>
            <picture>
              <source media="(max-width: 767px)" srcSet="/landing/hero-mobile.webp" />
              <img src="/landing/hero-desktop.webp" alt="Donna elegante che usa lo smartphone in un salotto di lusso con skyline notturno" className="w-full h-full object-cover" />
            </picture>
          </motion.div>
          <motion.div className="absolute inset-0 bg-black" style={{ opacity: heroDark }} />
          <div className="hidden md:block absolute inset-0" style={{ background: "linear-gradient(to right, rgba(13,13,13,0.9), rgba(13,13,13,0) 85%)" }} />
          <div className="md:hidden absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(13,13,13,0.85), rgba(13,13,13,0.6))" }} />
        </div>

        <CursorGlow />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div style={{ y: textY }} className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#E91E8C]/40 text-[#F5C6D6] text-xs uppercase tracking-[0.2em] mb-6">
              <ShieldCheck className="w-4 h-4" /> Commissione solo 15%
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black font-heading leading-[1.02] mb-6">
              <WordReveal el="span" text="Guadagna di più," className="block" />
              <WordReveal el="span" text="tieni di più." className="block text-[#E91E8C] text-glow" base={0.18} />
            </h1>
            <p className="text-lg text-white/70 max-w-xl mb-9 leading-relaxed">
              La piattaforma per creator che trattiene solo il <strong className="text-white">15%</strong> —
              contro il 20% degli altri, con pagamenti in 48 ore e supporto reale.
            </p>
            <div className="flex flex-wrap gap-4">
              <Magnetic>
                <Link
                  to="/register"
                  data-testid="hero-cta-creator"
                  className="cta-pulse group relative overflow-hidden inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#E91E8C] text-white font-semibold hover:bg-[#D8157B] transition-colors duration-300"
                >
                  <Sheen />
                  Diventa creator
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </Magnetic>
              <Magnetic>
                <a
                  href="#come-funziona"
                  className="group relative overflow-hidden inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-semibold hover:bg-white/5 transition-colors duration-300"
                >
                  <Sheen />
                  Scopri come funziona
                </a>
              </Magnetic>
            </div>
            <div className="flex items-center gap-8 mt-12">
              <div>
                <p className="text-3xl font-black font-heading text-[#E91E8C]"><CountUp to={15} suffix="%" /></p>
                <p className="text-xs text-white/50 uppercase tracking-wider">Commissione</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div>
                <p className="text-3xl font-black font-heading"><CountUp to={85} suffix="%" /></p>
                <p className="text-xs text-white/50 uppercase tracking-wider">Ti resta</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div>
                <p className="text-3xl font-black font-heading"><CountUp to={48} suffix="h" /></p>
                <p className="text-xs text-white/50 uppercase tracking-wider">Pagamenti</p>
              </div>
            </div>
          </motion.div>

          <motion.div style={{ y: imageY }} className="relative animate-fade-up" >
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 glow-fuchsia bg-[#0D0D0D]">
              <iframe
                src="/luvme-heart.html?v=2"
                title="Luvme — Cuore della Community"
                data-testid="hero-heart-iframe"
                loading="lazy"
                scrolling="no"
                className="w-full h-[440px] block"
                style={{ border: 0, background: "#0D0D0D" }}
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0D0D0D] to-transparent pointer-events-none" />
              <div className="absolute bottom-5 left-5 glass rounded-2xl border border-white/10 px-4 py-3 flex items-center gap-3 pointer-events-none">
                <BadgeCheck className="w-6 h-6 text-[#F5C6D6]" />
                <div>
                  <p className="text-sm font-semibold">Creator verificati</p>
                  <p className="text-xs text-white/60">Comunità sicura e premium</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chi siamo + pilastri di valore */}
      <section id="chi-siamo" className="sec-fx fx-glow-bl overflow-hidden py-20 px-5 sm:px-8 border-t border-white/10">
        <CursorGlow />
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <ZoomImage
              src="/landing/creator.webp"
              alt="Uomo elegante in abito scuro che usa lo smartphone con luci al neon fucsia"
              wrapperClassName="order-first lg:order-last w-full rounded-3xl"
              imgClassName="w-full h-auto object-cover"
              style={{ boxShadow: "0 0 60px rgba(233,30,140,0.35)" }}
            />
            <Reveal className="max-w-xl">
              <span className="text-sm text-white/50 uppercase tracking-[0.2em]">Chi siamo</span>
              <WordReveal el="h2" text="Costruito per i creator, non per le piattaforme." className="text-3xl sm:text-4xl font-bold font-heading mt-3 mb-5" />
              <p className="text-white/70 text-lg leading-relaxed">
                Luvme nasce con una missione semplice: dare ai creator il controllo e i guadagni che meritano.
                Non è solo una questione di commissioni più basse — è un ambiente elegante, sicuro e trasparente
                dove il tuo talento vale di più.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {PILLARS.map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard
                  testid={`pillar-${i}`}
                  className="h-full bg-[#1A1A1A] border border-white/10 rounded-3xl p-8 transition-colors duration-300 hover:border-[#E91E8C]/40"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#E91E8C]/15 flex items-center justify-center mb-5">
                    <p.icon className="w-6 h-6 text-[#E91E8C]" />
                  </div>
                  <h3 className="text-lg font-bold font-heading mb-2">{p.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{p.text}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Come funziona */}
      <section id="come-funziona" className="sec-fx fx-dots overflow-hidden py-24 px-5 sm:px-8">
        <CursorGlow />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <ZoomImage
              src="/landing/come-funziona.webp"
              alt="Mani eleganti con smartphone dallo schermo rosa luminoso su velluto scuro"
              wrapperClassName="w-full rounded-3xl aspect-[4/5]"
              imgClassName="w-full h-full object-cover"
              style={{ boxShadow: "0 0 60px rgba(233,30,140,0.35)" }}
            />
          </div>
          <div className="lg:col-span-3">
            <Reveal className="mb-10">
              <span className="text-sm text-white/50 uppercase tracking-[0.2em]">Come funziona</span>
              <WordReveal el="h2" text="In 4 semplici passi" className="text-3xl sm:text-4xl font-bold font-heading mt-3" />
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard
                  testid={`step-${i}`}
                  className="relative h-full bg-[#1A1A1A] border border-white/10 rounded-3xl p-8 transition-colors duration-300 hover:border-[#E91E8C]/40"
                >
                  <span className="text-6xl font-black font-heading text-white/5 absolute top-4 right-6">{i + 1}</span>
                  <div className="w-14 h-14 rounded-2xl bg-[#E91E8C]/15 flex items-center justify-center mb-5">
                    <s.icon className="w-7 h-7 text-[#E91E8C]" />
                  </div>
                  <h3 className="text-xl font-bold font-heading mb-2">{s.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{s.text}</p>
                </TiltCard>
              </Reveal>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* Funzionalità */}
      <section className="sec-fx fx-diagonal overflow-hidden py-24 px-5 sm:px-8 border-t border-white/10">
        <CursorGlow />
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-16">
            <span className="text-sm text-white/50 uppercase tracking-[0.2em]">Funzionalità</span>
            <WordReveal el="h2" text="Tutto ciò che ti serve" className="text-3xl sm:text-4xl font-bold font-heading mt-3" />
          </Reveal>
          <div className="grid md:grid-cols-6 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={i} delay={i * 0.1} className={i < 2 ? "md:col-span-3" : "md:col-span-2"}>
                <TiltCard
                  testid={`feature-${i}`}
                  className="h-full bg-[#1A1A1A] border border-white/10 rounded-3xl p-8 transition-colors duration-300 hover:border-[#E91E8C]/40"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#E91E8C]/15 flex items-center justify-center mb-5">
                    <f.icon className="w-6 h-6 text-[#E91E8C]" />
                  </div>
                  <h3 className="text-lg font-bold font-heading mb-2">{f.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{f.text}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Perché scegliere Luvme */}
      <section className="sec-fx fx-glow-tr overflow-hidden py-24 px-5 sm:px-8">
        <CursorGlow />
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center mb-16">
            <span className="text-sm text-white/50 uppercase tracking-[0.2em]">Perché Luvme</span>
            <WordReveal el="h2" text="Perché scegliere Luvme" className="text-3xl sm:text-4xl font-bold font-heading mt-3" />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_LUVME.map((w, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard
                  testid={`why-${i}`}
                  className="h-full bg-[#1A1A1A] border border-white/10 rounded-3xl p-8 transition-colors duration-300 hover:border-[#E91E8C]/40"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#E91E8C]/15 flex items-center justify-center mb-5">
                    <w.icon className="w-6 h-6 text-[#E91E8C]" />
                  </div>
                  <h3 className="text-lg font-bold font-heading mb-2">{w.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{w.text}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comparativa */}
      <section className="sec-fx fx-glow-center overflow-hidden py-24 px-5 sm:px-8">
        <CursorGlow />
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="text-sm text-white/50 uppercase tracking-[0.2em]">Confronto</span>
            <WordReveal el="h2" text="Luvme vs altre piattaforme" className="text-3xl sm:text-4xl font-bold font-heading mt-3" />
          </Reveal>
          <Reveal>
            <div className="rounded-3xl border border-white/10 overflow-hidden">
              <div className="grid grid-cols-3 bg-[#1A1A1A]">
                <div className="p-5" />
                <div className="p-5 text-center border-l border-white/10 bg-[#E91E8C]/10">
                  <span className="text-lg font-black font-heading text-[#E91E8C]">Luvme</span>
                </div>
                <div className="p-5 text-center border-l border-white/10">
                  <span className="text-lg font-semibold text-white/60">Altri</span>
                </div>
              </div>
              {COMPARISON.map((row, i) => (
                <div key={i} className="grid grid-cols-3 border-t border-white/10" data-testid={`compare-row-${i}`}>
                  <div className="p-5 text-sm text-white/80 flex items-center">{row.label}</div>
                  <div className="p-5 flex items-center justify-center border-l border-white/10 bg-[#E91E8C]/5">
                    {typeof row.luvme === "boolean" ? (
                      row.luvme ? <Check className="w-5 h-5 text-[#E91E8C]" /> : <Minus className="w-5 h-5 text-white/30" />
                    ) : (
                      <span className="font-bold text-[#E91E8C]">{row.luvme}</span>
                    )}
                  </div>
                  <div className="p-5 flex items-center justify-center border-l border-white/10">
                    {typeof row.others === "boolean" ? (
                      row.others ? <Check className="w-5 h-5 text-white/50" /> : <Minus className="w-5 h-5 text-white/30" />
                    ) : (
                      <span className="font-semibold text-white/50">{row.others}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-5 sm:px-8 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="text-sm text-white/50 uppercase tracking-[0.2em]">FAQ</span>
            <WordReveal el="h2" text="Domande frequenti" className="text-3xl sm:text-4xl font-bold font-heading mt-3" />
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQ.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-${i}`}
                  className="bg-[#1A1A1A] border border-white/10 rounded-2xl px-6"
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline hover:text-[#E91E8C]">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <Reveal className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden rounded-[2.5rem] border border-[#E91E8C]/40 bg-gradient-to-br from-[#E91E8C]/20 to-[#0D0D0D] p-12 sm:p-16 text-center glow-fuchsia">
            <div className="mesh-gradient absolute inset-0 opacity-40 pointer-events-none" />
            <div className="relative">
              <WordReveal el="h2" text="Pronti a guadagnare di più?" className="text-3xl sm:text-5xl font-black font-heading mb-5" />
              <p className="text-white/70 text-lg mb-8 max-w-xl mx-auto">Unisciti a Luvme oggi. Solo 15% di commissione, il resto è tutto tuo.</p>
              <Magnetic>
                <Link
                  to="/register"
                  data-testid="footer-cta-creator"
                  className="cta-pulse group relative overflow-hidden inline-flex items-center gap-2 px-9 py-4 rounded-full bg-[#E91E8C] text-white font-semibold hover:bg-[#D8157B] transition-colors duration-300"
                >
                  <Sheen />
                  Diventa creator <ArrowRight className="w-5 h-5" />
                </Link>
              </Magnetic>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Community band (pre-footer) */}
      <section className="relative overflow-hidden" data-testid="community-band">
        <ZoomImage
          src="/landing/community.webp"
          alt="Coppia elegante che sorseggia un drink in un lounge bar con luci al neon e skyline notturno"
          wrapperClassName="absolute inset-0"
          imgClassName="w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "rgba(13,13,13,0.7)" }} />
        <div className="relative max-w-3xl mx-auto text-center py-28 px-5 sm:px-8">
          <WordReveal el="h2" text="Una community che ti valorizza" className="text-3xl sm:text-5xl font-black font-heading mb-4" />
          <p className="text-white/75 text-lg mb-8">Unisciti ai creator che guadagnano di più e tengono di più.</p>
          <Magnetic>
            <Link
              to="/register"
              data-testid="community-cta-creator"
              className="group relative overflow-hidden inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#E91E8C] text-white font-semibold hover:bg-[#D8157B] transition-colors duration-300"
            >
              <Sheen />
              Diventa creator
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Magnetic>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <LuvmeIcon className="w-6 h-6" />
                <span className="text-xl font-black font-heading">Luvme</span>
              </div>
              <p className="text-sm text-white/50 max-w-xs">La piattaforma premium per creator con la commissione più bassa.</p>
            </div>
            <div className="flex flex-wrap gap-x-10 gap-y-3">
              <Link to="/supporto" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-support">Assistenza</Link>
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-terms">Termini di servizio</a>
              <a href="#" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-privacy">Privacy</a>
              <Link to="/supporto" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-contacts">Contatti</Link>
            </div>
          </div>
          <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-xs text-white/40">© 2026 Luvme. Tutti i diritti riservati.</p>
            <p className="text-xs text-[#F5C6D6] flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Solo per maggiorenni · Verifica età richiesta
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
